import { cn } from "@/lib/cn";
import { useDirtyForm } from "@/hooks/use-dirty-form";

export interface ProductTabDef {
  slug: string;
  label: string;
  count?: number | null;
  hidden?: boolean;
}

interface ProductTabsNavProps {
  tabs: ProductTabDef[];
  active: string;
  onChange: (slug: string) => void;
}

export function ProductTabsNav({ tabs, active, onChange }: ProductTabsNavProps) {
  const dirty = useDirtyForm();

  async function handleClick(slug: string) {
    if (slug === active) return;
    if (dirty.anyDirty) {
      const ok = await dirty.confirmDiscard();
      if (!ok) return;
    }
    onChange(slug);
  }

  return (
    <div className="border-b">
      <div className="flex items-center gap-1">
        {tabs.filter((t) => !t.hidden).map((t) => {
          const isActive = t.slug === active;
          return (
            <button
              key={t.slug}
              type="button"
              onClick={() => handleClick(t.slug)}
              className={cn(
                "relative px-4 py-2.5 text-sm font-medium flex items-center gap-1.5",
                "transition-colors hover:text-foreground",
                isActive
                  ? "text-foreground border-b-2 border-blue-500 -mb-px"
                  : "text-muted-foreground",
              )}
              data-testid={`product-tab-${t.slug}`}
            >
              {t.label}
              {typeof t.count === "number" && t.count > 0 && (
                <span className={cn(
                  "text-xs rounded-md px-1.5 py-0.5 min-w-[20px] inline-flex items-center justify-center",
                  isActive
                    ? "bg-blue-500/15 text-blue-600 dark:text-blue-400"
                    : "bg-muted text-muted-foreground",
                )}>
                  {t.count}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
